import { useEffect, useState, useRef } from "react";
import { api, formatINR } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import { Plus, X, GitCompareArrows, Award } from "lucide-react";
import AICtaBlock from "@/components/AICtaBlock";
import { track } from "@/lib/analytics";

export default function Compare() {
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState([]);
  const [rows, setRows] = useState([]);
  const startedRef = useRef(false); // ensures compare_started fires ONCE per session load

  useEffect(() => { api.get("/colleges").then((r) => setAll(r.data)).catch(() => {}); }, []);

  const addCollege = (id) => {
    if (!id) return;
    if (selected.includes(id)) return;
    if (selected.length >= 3) { toast.error("You can compare up to 3 colleges."); return; }
    // Fire compare_started only on the FIRST meaningful selection — not on page load.
    if (!startedRef.current) {
      startedRef.current = true;
      track("compare_started", { source: "compare_page" });
    }
    setSelected([...selected, id]);
  };
  const remove = (id) => setSelected(selected.filter((s) => s !== id));

  useEffect(() => {
    if (selected.length === 0) { setRows([]); return; }
    api.post("/colleges/compare", { ids: selected }).then((r) => {
      const ordered = selected.map((id) => r.data.find((c) => c.id === id)).filter(Boolean);
      setRows(ordered);
    });
  }, [selected]);

  const FIELDS = [
    { label: "Annual Fees", render: (c) => `₹${formatINR(c.fees?.min)} – ₹${formatINR(c.fees?.max)}` },
    { label: "Eligibility", render: (c) => c.eligibility },
    { label: "NAAC Grade", render: (c) => <span className="inline-flex items-center gap-1 rounded-full bg-[#FDF9F1] px-2.5 py-0.5 text-xs font-semibold text-[#C08E3B]"><Award className="h-3 w-3" /> {c.naacGrade}</span> },
    { label: "Placements", render: (c) => c.placementHighlights },
    { label: "Courses", render: (c) => (c.courses || []).join(", ") },
  ];

  const available = all.filter((c) => !selected.includes(c.id));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="compare-page">
      <SEOHead title="Compare Colleges" description="Compare up to 3 Pune colleges side by side on fees, NAAC, placements and courses." />
      <div>
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Side by side</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 flex items-center gap-3">
          <GitCompareArrows className="h-8 w-8 text-[#0B2A6B]" /> Compare Colleges
        </h1>
        <p className="mt-3 text-slate-600">Add up to 3 colleges to compare fees, eligibility, NAAC, placements and courses.</p>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <select
          data-testid="compare-select"
          className="h-11 w-full sm:w-auto max-w-full min-w-0 rounded-lg border border-slate-300 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-[#0B2A6B]"
          value=""
          onChange={(e) => addCollege(e.target.value)}
          disabled={selected.length >= 3}
        >
          <option value="">{selected.length >= 3 ? "Max 3 selected" : "+ Add a college to compare"}</option>
          {available.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <span className="text-sm text-slate-500">{selected.length}/3 selected</span>
      </div>

      {rows.length === 0 ? (
        <div className="mt-12 flex flex-col items-center rounded-2xl border-2 border-dashed border-slate-200 py-20 text-center" data-testid="compare-empty">
          <Plus className="h-10 w-10 text-slate-300" />
          <p className="mt-3 text-slate-500">Select colleges above to start comparing.</p>
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 shadow-sm" data-testid="compare-table">
          <table className="w-full min-w-[640px] bg-white text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="p-4 text-sm font-semibold text-slate-500 w-40">Criteria</th>
                {rows.map((c) => (
                  <th key={c.id} className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-display font-bold text-slate-900 text-sm">{c.name}</span>
                      <button data-testid={`compare-remove-${c.slug}`} onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FIELDS.map((f) => (
                <tr key={f.label} className="border-b border-slate-100 last:border-0">
                  <td className="p-4 text-sm font-semibold text-slate-600 align-top">{f.label}</td>
                  {rows.map((c) => (
                    <td key={c.id} className="p-4 text-sm text-slate-700 align-top">{f.render(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-8">
          <AICtaBlock ctaId="compare_after_table" variantStyle="slim" headline="Need an expert opinion on these colleges?" sub="Get a free, AI-matched recommendation tailored to your profile." buttonText="Ask AI" badges={false} />
        </div>
      )}
    </div>
  );
}
